import { Card } from "../../components/ui/Card";
import { RecentConversations } from "../../components/admin/RecentConversations";
import {
  Database,
  WholeWord,
  TextSearch,
  Loader2,
  CheckCircle,
  XCircle,
  Percent,
  Clock,
  Edit,
  Archive,
  Plus,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@clerk/clerk-react";
import { createApiClient } from "../../lib/api/client";

interface SearchResult {
  id: string;
  query: string;
  search_type: "SEMANTIC" | "KEYWORDS";
  total_results: number;
  results: Array<{ id: string; score: number }>;
  execution_time_ms: number;
  is_successful: boolean;
}

interface SearchResponse {
  results: SearchResult[];
  total: number;
  page: number;
  size: number;
  pages: number;
  has_next: boolean;
  has_prev: boolean;
}

interface SearchCounts {
  semantic: number;
  keywords: number;
  successful: number;
  failed: number;
}

interface DocumentCounts {
  total: {
    active_documents: number;
    vectorstore_records: number;
  };
}

interface DocumentActivity {
  id: string;
  title: string;
  action: "created" | "updated" | "archived";
  timestamp: string;
}

interface DocumentActivityResponse {
  activities: DocumentActivity[];
  total: number;
}

function useAnalyticsApi() {
  const { getToken } = useAuth();
  const api = createApiClient(() => getToken());

  return {
    async listSearches(page: number = 0, size: number = 10) {
      const response = await api.get<SearchResponse>("/analytics/searches", {
        params: { page, size },
      });
      return response.data;
    },
    async getTotalDocuments() {
      const response = await api.get<DocumentCounts>(
        "/analytics/stats/documents/total"
      );
      return response.data;
    },
    async getSearchCounts() {
      const response = await api.get<SearchCounts>(
        "/analytics/stats/searches/count"
      );
      return response.data;
    },
    async getRecentDocumentActivity() {
      const response = await api.get<DocumentActivityResponse>(
        "/analytics/documents/activity"
      );
      return response.data;
    },
  };
}

export function DashboardPage() {
  const analyticsApi = useAnalyticsApi();
  const {
    data: searchData,
    isLoading: isSearchesLoading,
    isError: isSearchesError,
  } = useQuery({
    queryKey: ["searches"],
    queryFn: () => analyticsApi.listSearches(),
    refetchOnMount: "always",
  });

  const {
    data: documentsData,
    isLoading: isDocumentsLoading,
    isError: isDocumentsError,
  } = useQuery({
    queryKey: ["documents", "total"],
    queryFn: () => analyticsApi.getTotalDocuments(),
    refetchOnMount: "always",
  });

  const {
    data: searchCountsData,
    isLoading: isSearchCountsLoading,
    isError: isSearchCountsError,
  } = useQuery({
    queryKey: ["searches", "counts"],
    queryFn: () => analyticsApi.getSearchCounts(),
    refetchOnMount: "always",
  });

  const {
    data: activityData,
    isLoading: isActivityLoading,
    isError: isActivityError,
  } = useQuery({
    queryKey: ["documents", "activity"],
    queryFn: () => analyticsApi.getRecentDocumentActivity(),
    refetchOnMount: "always",
  });

  const getActionIcon = (action: string) => {
    switch (action) {
      case "created":
        return <Plus className="h-4 w-4 text-green-500" />;
      case "updated":
        return <Edit className="h-4 w-4 text-blue-500" />;
      case "archived":
        return <Archive className="h-4 w-4 text-red-500" />;
      default:
        return null;
    }
  };

  const formatTimestamp = (timestamp: string) => {
    // Create a Date object from the timestamp
    // If timestamp doesn't end with 'Z', assume it's UTC and add 'Z'
    const dateString = timestamp.endsWith("Z") ? timestamp : timestamp + "Z";
    const date = new Date(dateString);

    // Convert to EST/EDT timezone
    const options: Intl.DateTimeFormatOptions = {
      timeZone: "America/New_York",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    };

    const formatter = new Intl.DateTimeFormat("en-CA", options);
    const formattedParts = formatter.formatToParts(date);

    // Reconstruct in YYYY-MM-DD HH:MM:SS format
    const datePart = `${formattedParts.find((p) => p.type === "year")?.value}-${
      formattedParts.find((p) => p.type === "month")?.value
    }-${formattedParts.find((p) => p.type === "day")?.value}`;
    const timePart = `${formattedParts.find((p) => p.type === "hour")?.value}:${
      formattedParts.find((p) => p.type === "minute")?.value
    }:${formattedParts.find((p) => p.type === "second")?.value}`;

    return `${datePart} ${timePart}`;
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Dashboard Overview</h2>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <Card>
          <div className="flex items-center gap-4">
            <div className="rounded-lg bg-blue-100 p-3">
              <Database className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Documents</p>
              {isDocumentsLoading ? (
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              ) : isDocumentsError ? (
                "Error"
              ) : (
                <div className="space-y-1">
                  <p className="text-2xl font-semibold text-gray-900">
                    {documentsData?.total.active_documents || 0}
                  </p>
                  {/* {documentsData?.total.active_documents !==
                    documentsData?.total.vectorstore_records && (
                    <p className="text-xs text-gray-500">
                      {documentsData?.total.vectorstore_records || 0} indexés (
                      {documentsData?.total.active_documents
                        ? (
                            (documentsData.total.vectorstore_records /
                              documentsData.total.active_documents) *
                            100
                          ).toFixed(2)
                        : "0.00"}
                      %)
                    </p>
                  )} */}
                </div>
              )}
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-4">
            <div className="rounded-lg bg-purple-100 p-3">
              <TextSearch className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">
                Recherches sémantiques
              </p>
              <p className="text-2xl font-semibold text-gray-900">
                {isSearchCountsLoading ? (
                  <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                ) : isSearchCountsError ? (
                  "Error"
                ) : (
                  searchCountsData?.semantic || 0
                )}
              </p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-4">
            <div className="rounded-lg bg-yellow-100 p-3">
              <WholeWord className="h-6 w-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">
                Recherches par mots-clés
              </p>
              <p className="text-2xl font-semibold text-gray-900">
                {isSearchCountsLoading ? (
                  <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                ) : isSearchCountsError ? (
                  "Error"
                ) : (
                  searchCountsData?.keywords || 0
                )}
              </p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-4">
            <div className="rounded-lg bg-green-100 p-3">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">
                Recherches réussies
              </p>
              <p className="text-2xl font-semibold text-gray-900">
                {isSearchCountsLoading ? (
                  <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                ) : isSearchCountsError ? (
                  "Error"
                ) : (
                  searchCountsData?.successful || 0
                )}
              </p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-4">
            <div className="rounded-lg bg-red-100 p-3">
              <XCircle className="h-6 w-6 text-red-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">
                Recherches échouées
              </p>
              <p className="text-2xl font-semibold text-gray-900">
                {isSearchCountsLoading ? (
                  <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                ) : isSearchCountsError ? (
                  "Error"
                ) : (
                  searchCountsData?.failed || 0
                )}
              </p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-4">
            <div className="rounded-lg bg-indigo-100 p-3">
              <Percent className="h-6 w-6 text-indigo-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">
                Taux de réussite
              </p>
              <p className="text-2xl font-semibold text-gray-900">
                {isSearchCountsLoading ? (
                  <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                ) : isSearchCountsError ? (
                  "Error"
                ) : (
                  searchCountsData &&
                  (searchCountsData.successful + searchCountsData.failed > 0
                    ? `${Math.round(
                        (searchCountsData.successful /
                          (searchCountsData.successful +
                            searchCountsData.failed)) *
                          100
                      )}%`
                    : "N/A")
                )}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Recent Conversations */}
      <RecentConversations limit={5} />

      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Clock className="h-5 w-5 text-gray-500" />
          <h3 className="text-lg font-semibold text-gray-900">
            Recent Document Activity
          </h3>
        </div>

        <Card>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                    Action
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                    Title
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                    When
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {isActivityLoading ? (
                  <tr>
                    <td
                      colSpan={3}
                      className="px-4 py-4 text-center text-sm text-gray-500"
                    >
                      <Loader2 className="mx-auto h-6 w-6 animate-spin text-gray-400" />
                    </td>
                  </tr>
                ) : isActivityError ? (
                  <tr>
                    <td
                      colSpan={3}
                      className="px-4 py-4 text-center text-sm text-gray-500"
                    >
                      Failed to load recent activity
                    </td>
                  </tr>
                ) : activityData?.activities.length === 0 ? (
                  <tr>
                    <td
                      colSpan={3}
                      className="px-4 py-4 text-center text-sm text-gray-500"
                    >
                      No recent activity
                    </td>
                  </tr>
                ) : (
                  activityData?.activities.map((activity) => (
                    <tr key={activity.id}>
                      <td className="whitespace-nowrap px-4 py-3 text-sm">
                        <div className="flex items-center gap-1.5">
                          {getActionIcon(activity.action)}
                          <span className="capitalize">{activity.action}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">{activity.title}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">
                        {formatTimestamp(activity.timestamp)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Recent Searches */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <TextSearch className="h-5 w-5 text-gray-500" />
          <h3 className="text-lg font-semibold text-gray-900">
            Recherches récentes
          </h3>
        </div>

        <Card>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                    Query
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                    Results
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {isSearchesLoading ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="px-4 py-4 text-center text-sm text-gray-500"
                    >
                      <Loader2 className="mx-auto h-6 w-6 animate-spin text-gray-400" />
                    </td>
                  </tr>
                ) : isSearchesError ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="px-4 py-4 text-center text-sm text-gray-500"
                    >
                      Failed to load recent searches
                    </td>
                  </tr>
                ) : searchData?.results.length === 0 ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="px-4 py-4 text-center text-sm text-gray-500"
                    >
                      No recent searches
                    </td>
                  </tr>
                ) : (
                  searchData?.results.map((search) => {
                    const highestScore = search.results.reduce(
                      (max, result) => Math.max(max, result.score),
                      0
                    );

                    return (
                      <tr key={search.id}>
                        <td className="whitespace-nowrap px-4 py-3 text-sm">
                          <span
                            className={`rounded-full px-2 py-1 text-xs font-medium ${
                              search.search_type === "SEMANTIC"
                                ? "bg-purple-100 text-purple-600"
                                : "bg-yellow-100 text-yellow-600"
                            }`}
                          >
                            {search.search_type}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          {search.query}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">
                          {search.total_results} results •{" "}
                          {search.execution_time_ms}ms
                          {search.is_successful && highestScore > 0 && (
                            <span className="ml-2 rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-600">
                              Score: {highestScore.toFixed(2)}
                            </span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm">
                          <span
                            className={`rounded-full px-2 py-1 text-xs font-medium ${
                              search.is_successful
                                ? "bg-green-100 text-green-600"
                                : "bg-red-100 text-red-600"
                            }`}
                          >
                            {search.is_successful ? "Success" : "Failed"}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}
